/**
 * /settings/dimensions — GL dimension management.
 *
 * Admins define dimensions (OFFICE, DIVISION, PRACTICE_AREA, etc.) and
 * manage their value trees. Each dimension and its values are then
 * available as tags on journal entries, AP bills, AR invoices, etc.
 *
 * Layout:
 *   Left rail  — list of all dimensions with value counts + "New" button
 *   Right panel — selected dimension: value tree + add/edit/deactivate
 *
 * All writes are admin-only (enforced by backend). Reads are open so
 * accountants can see the setup.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import {
  glDimensionDetailQueryOptions,
  glDimensionKeys,
  glDimensionsQueryOptions,
} from "@/lib/queries";
import type { GlDimension, GlDimensionListItem, GlDimensionValue } from "@/lib/types";
import { useToast } from "@/components/Toaster";

export const Route = createFileRoute("/_auth/settings/dimensions")({
  component: DimensionsSettingsPage,
});

// ── Page ──────────────────────────────────────────────────────────────────────

function DimensionsSettingsPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showNewDimDialog, setShowNewDimDialog] = useState(false);

  const { data: dimensions = [], isLoading } = useQuery(glDimensionsQueryOptions());

  return (
    <div className="flex h-full overflow-hidden">

      {/* ── Left rail ── */}
      <div className="w-64 flex-shrink-0 border-r border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-gray-800 flex items-center justify-between">
          <div>
            <h2 className="text-[13px] font-semibold text-slate-800 dark:text-slate-100">Dimensions</h2>
            <p className="text-[11px] text-slate-400 mt-0.5">Org classification segments</p>
          </div>
          <button
            onClick={() => setShowNewDimDialog(true)}
            className="w-7 h-7 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center text-lg leading-none transition-colors"
            title="New dimension"
          >
            +
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-1">
          {isLoading && (
            <div className="px-4 py-8 text-center text-[12px] text-slate-400">Loading…</div>
          )}
          {!isLoading && dimensions.length === 0 && (
            <div className="px-4 py-8 text-center">
              <div className="text-2xl mb-2">🏷</div>
              <p className="text-[12px] text-slate-500 dark:text-slate-400">No dimensions yet</p>
              <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">
                Create one to start classifying transactions by office, division, or practice area.
              </p>
            </div>
          )}
          {dimensions.map((dim) => (
            <DimListItem
              key={dim.id}
              dim={dim}
              isActive={dim.id === selectedId}
              onClick={() => setSelectedId(dim.id)}
            />
          ))}
        </div>

        {/* Legend */}
        <div className="px-4 py-3 border-t border-slate-200 dark:border-gray-800">
          <p className="text-[10px] text-slate-400 dark:text-slate-500 leading-relaxed">
            Dimensions tag transactions for reporting. Examples: Office, Division, Practice Area, Fund, Region.
          </p>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-gray-950">
        {selectedId ? (
          <DimensionDetail key={selectedId} dimensionId={selectedId} />
        ) : (
          <EmptyState onNew={() => setShowNewDimDialog(true)} />
        )}
      </div>

      {/* ── New dimension dialog ── */}
      {showNewDimDialog && (
        <NewDimensionDialog
          onClose={() => setShowNewDimDialog(false)}
          onCreated={(id) => { setShowNewDimDialog(false); setSelectedId(id); }}
        />
      )}
    </div>
  );
}

// ── Left rail item ────────────────────────────────────────────────────────────

function DimListItem({
  dim, isActive, onClick,
}: {
  dim: GlDimensionListItem;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-2 transition-colors ${
        isActive
          ? "bg-indigo-50 dark:bg-indigo-950/40 border-r-2 border-indigo-600"
          : "hover:bg-slate-50 dark:hover:bg-gray-800/50"
      } ${!dim.is_active ? "opacity-50" : ""}`}
    >
      <div className="min-w-0">
        <div className={`text-[13px] font-medium truncate ${isActive ? "text-indigo-700 dark:text-indigo-300" : "text-slate-700 dark:text-slate-200"}`}>
          {dim.name}
        </div>
        <div className="text-[10px] font-mono text-slate-400 dark:text-slate-500 mt-0.5">
          {dim.code}
          {dim.is_required && <span className="ml-1.5 text-amber-500">required</span>}
          {!dim.is_active && <span className="ml-1.5 text-slate-400">inactive</span>}
        </div>
      </div>
      <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${
        isActive
          ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300"
          : "bg-slate-100 dark:bg-gray-800 text-slate-500 dark:text-slate-400"
      }`}>
        {dim.value_count}
      </span>
    </button>
  );
}

// ── Dimension detail panel ────────────────────────────────────────────────────

function DimensionDetail({ dimensionId }: { dimensionId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [showAddValue, setShowAddValue] = useState(false);
  const [addParentId, setAddParentId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<GlDimensionValue | null>(null);
  const [showEditDim, setShowEditDim] = useState(false);

  const { data: dim, isLoading } = useQuery(glDimensionDetailQueryOptions(dimensionId));

  const deactivateDim = useMutation({
    mutationFn: () => api(`/api/settings/dimensions/${dimensionId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: glDimensionKeys.list() });
      qc.invalidateQueries({ queryKey: glDimensionKeys.detail(dimensionId) });
      toast.success("Dimension deactivated");
    },
    onError: () => toast.error("Failed to deactivate"),
  });

  const toggleRequired = useMutation({
    mutationFn: (is_required: boolean) =>
      api<GlDimension>(`/api/settings/dimensions/${dimensionId}`, {
        method: "PATCH",
        body: { is_required },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: glDimensionKeys.list() });
      qc.invalidateQueries({ queryKey: glDimensionKeys.detail(dimensionId) });
    },
    onError: () => toast.error("Failed to update"),
  });

  if (isLoading) return <div className="p-8 text-sm text-slate-400">Loading…</div>;
  if (!dim) return null;

  return (
    <div className="p-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{dim.name}</h2>
            <span className="font-mono text-[11px] bg-slate-100 dark:bg-gray-800 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded">
              {dim.code}
            </span>
            {!dim.is_active && (
              <span className="text-[11px] bg-slate-100 text-slate-400 px-2 py-0.5 rounded">Inactive</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[12px] text-slate-500 dark:text-slate-400">
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={dim.is_required}
                onChange={(e) => toggleRequired.mutate(e.target.checked)}
                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Required on every transaction
            </label>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowEditDim(true)}
            className="px-3 py-1.5 text-[12px] font-medium border border-slate-200 dark:border-gray-700 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors"
          >
            Rename
          </button>
          {dim.is_active && (
            <button
              onClick={() => {
                if (confirm(`Deactivate "${dim.name}"? Existing tagged data is preserved.`))
                  deactivateDim.mutate();
              }}
              className="px-3 py-1.5 text-[12px] font-medium border border-red-200 dark:border-red-900 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
            >
              Deactivate
            </button>
          )}
        </div>
      </div>

      {/* Values section */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[13px] font-semibold text-slate-700 dark:text-slate-200">
          Values
          <span className="ml-2 text-[11px] font-normal text-slate-400 dark:text-slate-500">
            {dim.values.length} total
          </span>
        </h3>
        <button
          onClick={() => { setAddParentId(null); setShowAddValue(true); }}
          className="flex items-center gap-1 px-3 py-1.5 text-[12px] font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
        >
          + Add value
        </button>
      </div>

      {dim.values.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 dark:border-gray-700 py-10 text-center">
          <div className="text-2xl mb-2">📂</div>
          <p className="text-[13px] text-slate-500 dark:text-slate-400">No values yet</p>
          <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">
            Add values like "New York", "Chicago", "Los Angeles"
          </p>
          <button
            onClick={() => { setAddParentId(null); setShowAddValue(true); }}
            className="mt-3 text-[12px] text-indigo-600 hover:underline"
          >
            Add first value →
          </button>
        </div>
      )}

      {dim.values.length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-slate-200 dark:border-gray-800 overflow-hidden">
          {dim.values.map((v, i) => (
            <ValueRow
              key={v.id}
              value={v}
              isLast={i === dim.values.length - 1}
              depth={0}
              dimensionId={dimensionId}
              onEdit={(val) => setEditingValue(val)}
              onAddChild={(parentId) => { setAddParentId(parentId); setShowAddValue(true); }}
            />
          ))}
        </div>
      )}

      {/* Dialogs */}
      {showAddValue && (
        <AddValueDialog
          dimensionId={dimensionId}
          parentId={addParentId}
          parentName={addParentId ? findValue(dim.values, addParentId)?.name : undefined}
          onClose={() => { setShowAddValue(false); setAddParentId(null); }}
        />
      )}
      {editingValue && (
        <EditValueDialog
          value={editingValue}
          onClose={() => setEditingValue(null)}
        />
      )}
      {showEditDim && (
        <EditDimensionDialog
          dim={dim}
          onClose={() => setShowEditDim(false)}
        />
      )}
    </div>
  );
}

// ── Value row (recursive) ─────────────────────────────────────────────────────

function ValueRow({
  value, isLast, depth, dimensionId, onEdit, onAddChild,
}: {
  value: GlDimensionValue;
  isLast: boolean;
  depth: number;
  dimensionId: string;
  onEdit: (v: GlDimensionValue) => void;
  onAddChild: (parentId: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const deactivate = useMutation({
    mutationFn: () =>
      api(`/api/settings/dimensions/values/${value.id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: glDimensionKeys.list() });
      qc.invalidateQueries({ queryKey: glDimensionKeys.detail(dimensionId) });
      toast.success(`"${value.name}" deactivated`);
    },
    onError: () => toast.error("Failed to deactivate"),
  });

  return (
    <>
      <div
        className={`flex items-center gap-3 px-4 py-2.5 group transition-colors hover:bg-slate-50 dark:hover:bg-gray-800/50 ${
          !isLast || value.children.length > 0 ? "border-b border-slate-100 dark:border-gray-800" : ""
        } ${!value.is_active ? "opacity-40" : ""}`}
        style={{ paddingLeft: `${16 + depth * 24}px` }}
      >
        {/* Hierarchy indent line */}
        {depth > 0 && (
          <span className="text-slate-300 dark:text-slate-600 flex-shrink-0 text-sm">└</span>
        )}

        {/* Code + name */}
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <span className="font-mono text-[11px] bg-slate-100 dark:bg-gray-800 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded flex-shrink-0">
            {value.code}
          </span>
          <span className="text-[13px] text-slate-700 dark:text-slate-200 truncate">{value.name}</span>
          {!value.is_active && (
            <span className="text-[10px] text-slate-400 flex-shrink-0">inactive</span>
          )}
          {value.children.length > 0 && (
            <span className="text-[10px] text-slate-400 flex-shrink-0">
              {value.children.length} sub-value{value.children.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Actions — show on hover */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <ActionBtn onClick={() => onAddChild(value.id)} title="Add child value">
            + sub
          </ActionBtn>
          <ActionBtn onClick={() => onEdit(value)} title="Edit name">
            Edit
          </ActionBtn>
          {value.is_active && (
            <ActionBtn
              onClick={() => {
                if (confirm(`Deactivate "${value.name}"?`)) deactivate.mutate();
              }}
              title="Deactivate"
              danger
            >
              Off
            </ActionBtn>
          )}
        </div>
      </div>

      {/* Children */}
      {value.children.map((child, i) => (
        <ValueRow
          key={child.id}
          value={child}
          isLast={i === value.children.length - 1}
          depth={depth + 1}
          dimensionId={dimensionId}
          onEdit={onEdit}
          onAddChild={onAddChild}
        />
      ))}
    </>
  );
}

// ── Dialogs ───────────────────────────────────────────────────────────────────

function NewDimensionDialog({
  onClose, onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [required, setRequired] = useState(false);
  const [error, setError] = useState("");

  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>("/api/settings/dimensions", {
        method: "POST",
        body: { code, name, is_required: required },
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: glDimensionKeys.list() });
      toast.success(`Dimension "${name}" created`);
      onCreated(data.id);
    },
    onError: (e: Error) => setError(e.message || "Failed to create"),
  });

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    setError("");
    if (!code.trim()) { setError("Code is required"); return; }
    if (!name.trim()) { setError("Name is required"); return; }
    create.mutate();
  };

  return (
    <Dialog onClose={onClose} title="New dimension">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-[12px] font-medium text-slate-700 dark:text-slate-200 mb-1">
            Code <span className="text-slate-400 font-normal">(short identifier, uppercase)</span>
          </label>
          <input
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
            placeholder="OFFICE"
            maxLength={40}
            className={inputCls}
          />
          <p className="text-[10px] text-slate-400 mt-1">
            Used internally — cannot be changed after creation. E.g. OFFICE, DIVISION, PRACTICE_AREA
          </p>
        </div>
        <div>
          <label className="block text-[12px] font-medium text-slate-700 dark:text-slate-200 mb-1">
            Display name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Office"
            maxLength={120}
            className={inputCls}
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
            className="rounded border-slate-300 text-indigo-600"
          />
          <span className="text-[12px] text-slate-700 dark:text-slate-200">
            Required on every transaction
          </span>
        </label>
        {error && <p className="text-[12px] text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={secondaryCls}>Cancel</button>
          <button type="submit" disabled={create.isPending} className={primaryCls}>
            {create.isPending ? "Creating…" : "Create dimension"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function AddValueDialog({
  dimensionId, parentId, parentName, onClose,
}: {
  dimensionId: string;
  parentId: string | null;
  parentName?: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  const add = useMutation({
    mutationFn: () =>
      api(`/api/settings/dimensions/${dimensionId}/values`, {
        method: "POST",
        body: { code, name, parent_id: parentId ?? undefined },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: glDimensionKeys.list() });
      qc.invalidateQueries({ queryKey: glDimensionKeys.detail(dimensionId) });
      toast.success(`Value "${name}" added`);
      onClose();
    },
    onError: (e: Error) => setError(e.message || "Failed to add value"),
  });

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    setError("");
    if (!code.trim()) { setError("Code is required"); return; }
    if (!name.trim()) { setError("Name is required"); return; }
    add.mutate();
  };

  return (
    <Dialog
      onClose={onClose}
      title={parentName ? `Add value under "${parentName}"` : "Add value"}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-[12px] font-medium text-slate-700 dark:text-slate-200 mb-1">
            Code
          </label>
          <input
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
            placeholder="NYC"
            maxLength={40}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-[12px] font-medium text-slate-700 dark:text-slate-200 mb-1">
            Display name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="New York"
            maxLength={255}
            className={inputCls}
          />
        </div>
        {error && <p className="text-[12px] text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={secondaryCls}>Cancel</button>
          <button type="submit" disabled={add.isPending} className={primaryCls}>
            {add.isPending ? "Adding…" : "Add value"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function EditValueDialog({
  value, onClose,
}: {
  value: GlDimensionValue;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(value.name);
  const [error, setError] = useState("");

  const update = useMutation({
    mutationFn: () =>
      api(`/api/settings/dimensions/values/${value.id}`, {
        method: "PATCH",
        body: { name },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: glDimensionKeys.list() });
      qc.invalidateQueries({ queryKey: ["gl-dimensions", "detail"] });
      toast.success("Value updated");
      onClose();
    },
    onError: (e: Error) => setError(e.message || "Failed to update"),
  });

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    setError("");
    if (!name.trim()) { setError("Name is required"); return; }
    update.mutate();
  };

  return (
    <Dialog onClose={onClose} title={`Edit "${value.code}"`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-[12px] font-medium text-slate-700 dark:text-slate-200 mb-1">
            Code <span className="text-slate-400 font-normal">(cannot be changed)</span>
          </label>
          <input value={value.code} disabled className={`${inputCls} opacity-50 cursor-not-allowed`} />
        </div>
        <div>
          <label className="block text-[12px] font-medium text-slate-700 dark:text-slate-200 mb-1">
            Display name
          </label>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={255}
            className={inputCls}
          />
        </div>
        {error && <p className="text-[12px] text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={secondaryCls}>Cancel</button>
          <button type="submit" disabled={update.isPending} className={primaryCls}>
            {update.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function EditDimensionDialog({
  dim, onClose,
}: {
  dim: GlDimension;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(dim.name);
  const [error, setError] = useState("");

  const update = useMutation({
    mutationFn: () =>
      api(`/api/settings/dimensions/${dim.id}`, {
        method: "PATCH",
        body: { name },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: glDimensionKeys.list() });
      qc.invalidateQueries({ queryKey: glDimensionKeys.detail(dim.id) });
      toast.success("Dimension renamed");
      onClose();
    },
    onError: (e: Error) => setError(e.message || "Failed to rename"),
  });

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    setError("");
    if (!name.trim()) { setError("Name is required"); return; }
    update.mutate();
  };

  return (
    <Dialog onClose={onClose} title="Rename dimension">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-[12px] font-medium text-slate-700 dark:text-slate-200 mb-1">
            Code <span className="text-slate-400 font-normal">(cannot be changed)</span>
          </label>
          <input value={dim.code} disabled className={`${inputCls} opacity-50 cursor-not-allowed`} />
        </div>
        <div>
          <label className="block text-[12px] font-medium text-slate-700 dark:text-slate-200 mb-1">
            Display name
          </label>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            className={inputCls}
          />
        </div>
        {error && <p className="text-[12px] text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={secondaryCls}>Cancel</button>
          <button type="submit" disabled={update.isPending} className={primaryCls}>
            {update.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ onNew }: { onNew: () => void }) {
  const EXAMPLES = [
    { code: "OFFICE", name: "Office", examples: "New York · Los Angeles · Chicago · London" },
    { code: "DIVISION", name: "Division", examples: "Commercial · Residential · Industrial" },
    { code: "PRACTICE_AREA", name: "Practice Area", examples: "Architecture · Structural · MEP · Civil" },
    { code: "REGION", name: "Region", examples: "Northeast · Southeast · West · International" },
    { code: "FUND", name: "Fund", examples: "Operating · Capital · Reserve" },
    { code: "DEPARTMENT", name: "Department", examples: "Finance · Operations · HR · Marketing" },
  ];

  return (
    <div className="p-8 max-w-2xl">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
          Org classification dimensions
        </h2>
        <p className="text-[13px] text-slate-500 dark:text-slate-400">
          Dimensions let you tag every transaction with your firm's organization structure —
          then slice any report by office, division, practice area, or any custom segment you define.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        {EXAMPLES.map((ex) => (
          <div
            key={ex.code}
            className="rounded-xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-[10px] bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 px-1.5 py-0.5 rounded">
                {ex.code}
              </span>
              <span className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">
                {ex.name}
              </span>
            </div>
            <p className="text-[11px] text-slate-400 dark:text-slate-500">{ex.examples}</p>
          </div>
        ))}
      </div>

      <button
        onClick={onNew}
        className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] font-medium rounded-lg transition-colors"
      >
        + Create your first dimension
      </button>
    </div>
  );
}

// ── Shared primitives ─────────────────────────────────────────────────────────

function Dialog({
  title, children, onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-6 border border-slate-200 dark:border-gray-800">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[15px] font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors text-lg"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ActionBtn({
  children, onClick, title, danger = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title?: string;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`px-2 py-1 text-[10px] font-medium rounded border transition-colors ${
        danger
          ? "border-red-200 text-red-500 hover:bg-red-50 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950"
          : "border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-gray-700 dark:text-slate-400 dark:hover:bg-gray-800"
      }`}
    >
      {children}
    </button>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function findValue(values: GlDimensionValue[], id: string): GlDimensionValue | undefined {
  for (const v of values) {
    if (v.id === id) return v;
    const found = findValue(v.children, id);
    if (found) return found;
  }
}

const inputCls =
  "w-full px-3 py-2 text-[13px] border border-slate-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent";

const primaryCls =
  "px-4 py-2 text-[13px] font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

const secondaryCls =
  "px-4 py-2 text-[13px] font-medium border border-slate-200 dark:border-gray-700 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors";
